// IP-based analytics exemption.
//
// On boot the app asks the API whether the visitor's IP is on the exempt list
// (backend_api/analytics_exempt_ips.json — the owner's home/office IPs). While
// the answer is "pending" no third-party tag may load; once it settles:
//   - exempt -> the existing per-browser kill switch (cd_analytics_opt_out,
//     honored by ga4.js and pixel.js) is set for the session and PostHog is
//     opted out, so no GA4/Ads/PostHog/Pixel traffic ever leaves the browser.
//   - clear  -> analytics initializes exactly as before.
// Any relay events that race ahead of the answer are dropped server-side by
// the same IP list (see /api/eventFlow), so exempt sessions never contaminate
// the numbers even mid-race. Fails open: if the API cannot answer within the
// timeout, the visitor counts as a normal user.
import serverIp from './serverConfig';
import { posthogOptOut } from './posthog';

const EXEMPTION_ENDPOINT = `${serverIp}/api/analyticsExemption`;
const SESSION_CACHE_KEY = 'cd_analytics_ip_exempt_v1';
const OPT_OUT_KEY = 'cd_analytics_opt_out';
const DEFAULT_TIMEOUT_MS = 2500;

let state = 'pending'; // 'pending' | 'exempt' | 'clear'
let resolutionPromise = null;
let settleListeners = [];

const hasWindow = () => typeof window !== 'undefined';

const readSession = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const removeSession = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const applyExempt = () => {
  state = 'exempt';
  writeSession(SESSION_CACHE_KEY, '1');
  writeSession(OPT_OUT_KEY, '1');
  try {
    posthogOptOut();
  } catch {}
};

const applyClear = () => {
  if (state === 'exempt') return;
  state = 'clear';
  // The opt-out flag belongs to the IP verdict only when accompanied by our
  // marker. Clear that stale pair when a fresh request comes from a different
  // network, while preserving an explicit localStorage user opt-out.
  if (readSession(SESSION_CACHE_KEY) === '1') {
    removeSession(OPT_OUT_KEY);
  }
  removeSession(SESSION_CACHE_KEY);
};

const initializeFromEnvironment = () => {
  if (!hasWindow()) {
    state = 'clear';
    return;
  }

  // Never trust an IP verdict across page loads. A visitor can switch VPNs or
  // networks inside the same browser session; reusing the old exempt verdict
  // would silently keep PostHog disabled from the new, non-exempt IP. Remove
  // only the session opt-out that our marker proves we created, then let the
  // fresh API check below decide this page.
  if (readSession(SESSION_CACHE_KEY) === '1') {
    removeSession(OPT_OUT_KEY);
  }
  removeSession(SESSION_CACHE_KEY);

  // Jest has no exemption API; keep the legacy code paths fully live in tests.
  if (process.env.NODE_ENV === 'test') {
    state = 'clear';
  }
};

initializeFromEnvironment();

const notifySettled = () => {
  const listeners = settleListeners;
  settleListeners = [];
  listeners.forEach((callback) => {
    try {
      callback(state);
    } catch {}
  });
};

export const getAnalyticsExemptionState = () => state;

// Runs the callback once the exemption is known (immediately when already
// settled). Used to hold third-party tag loading until the IP check answers.
export const onAnalyticsExemptionSettled = (callback) => {
  if (typeof callback !== 'function') return;
  if (state !== 'pending') {
    try {
      callback(state);
    } catch {}
    return;
  }
  settleListeners.push(callback);
};

export const resolveAnalyticsExemption = ({ timeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  if (!hasWindow() || typeof fetch !== 'function') {
    applyClear();
    notifySettled();
    return Promise.resolve(state);
  }
  if (state !== 'pending') return Promise.resolve(state);
  if (resolutionPromise) return resolutionPromise;

  resolutionPromise = new Promise((resolve) => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      notifySettled();
      resolve(state);
    };
    const timer = window.setTimeout(() => {
      applyClear();
      finish();
    }, timeoutMs);

    fetch(EXEMPTION_ENDPOINT, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit'
    })
      .then((response) => (response && response.ok ? response.json() : null))
      .then((data) => {
        window.clearTimeout(timer);
        if (data && data.exempt === true) {
          // Also honored after a timeout already failed open: better to stop
          // tracking late than to keep contaminating the numbers.
          applyExempt();
        } else {
          applyClear();
        }
        finish();
      })
      .catch(() => {
        window.clearTimeout(timer);
        applyClear();
        finish();
      });
  });
  return resolutionPromise;
};

// Test-only escape hatch: puts the module back into the pre-resolution state.
export const resetAnalyticsExemptionForTests = () => {
  if (process.env.NODE_ENV !== 'test') return;
  state = 'pending';
  resolutionPromise = null;
  settleListeners = [];
};
